import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { findWork, WORK } from "@/lib/work";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { SiteFooter } from "@/components/site-footer";
import { ArrowLeft, ArrowUpRight } from "@/components/icons";
import { BrowserFrame } from "@/components/work/device-frames";
import { WorkStage } from "@/components/work/work-stage";
import { WorkTile } from "@/components/work/work-tile";

export const Route = createFileRoute("/work/$slug")({
  loader: ({ params }) => {
    const item = findWork(params.slug);
    if (!item) throw notFound();
    return { item };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Case study not found" }, { name: "robots", content: "noindex" }] };
    }
    const w = loaderData.item;
    const url = `https://vibing.inc/work/${params.slug}`;
    const title = `${w.name}: ${w.title} | Vibing Co`;
    return {
      meta: [
        { title },
        { name: "description", content: w.summary },
        { property: "og:title", content: `${w.name}: ${w.title}` },
        { property: "og:description", content: w.summary },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: w.name,
            headline: w.title,
            about: w.category,
            description: w.summary,
            url,
            sameAs: w.url,
            creator: { "@type": "Organization", name: "the Vibing Company", url: "https://vibing.inc" },
          }),
        },
      ],
    };
  },
  component: WorkDetail,
});

const EYEBROW = "font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground";

function WorkDetail() {
  const { item: w } = Route.useLoaderData();
  const others = WORK.filter((o) => o.slug !== w.slug).slice(0, 3);
  const external = !w.url.startsWith("https://vibing.inc");

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
        <section className="mx-auto max-w-[1400px] px-6 pb-12 pt-[clamp(9rem,18vh,13rem)] md:px-10 md:pb-16">
          <Link
            to="/work"
            className="inline-flex min-h-11 items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft aria-hidden className="h-4 w-4" />
            All work
          </Link>
          <p className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-display text-lg tracking-[-0.02em]">{w.name}</span>
            <span className={EYEBROW}>
              {w.category} · {w.year}
            </span>
          </p>
          <h1
            className="mt-4 max-w-[20ch] font-display leading-[1.02] tracking-[-0.035em]"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
          >
            {w.title}
          </h1>
          <p className="mt-6 max-w-[58ch] text-pretty text-xl leading-snug text-muted-foreground md:text-2xl">
            {w.summary}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
            <TalkToJonButton variant="dark" />
            <a
              href={w.url}
              target={external ? "_blank" : undefined}
              rel={external ? "noopener noreferrer" : undefined}
              className="group inline-flex min-h-11 items-center gap-1.5 font-display text-base underline decoration-border underline-offset-4 transition-colors hover:decoration-foreground"
            >
              {external ? `Visit ${w.host}` : "Product page"}
              <ArrowUpRight
                aria-hidden
                className="h-4 w-4 transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </div>
        </section>

        <section aria-label="The product" className="mx-auto max-w-[1400px] px-6 md:px-10">
          <WorkStage item={w} />
        </section>

        <section className="mx-auto grid max-w-[1400px] gap-12 px-6 py-16 md:grid-cols-12 md:px-10 md:py-24">
          <div className="md:col-span-4">
            <dl className="grid grid-cols-2 gap-8 md:grid-cols-1">
              {w.facts.map((f) => (
                <div key={f.label}>
                  <dt className={EYEBROW}>{f.label}</dt>
                  <dd
                    className="mt-2 font-display leading-none tracking-[-0.04em]"
                    style={{ fontSize: "clamp(1.9rem, 3.4vw, 2.75rem)" }}
                  >
                    {f.value}
                  </dd>
                </div>
              ))}
            </dl>
            <div className="mt-10">
              <p className={EYEBROW}>Stack</p>
              <ul className="mt-3 flex flex-wrap gap-2">
                {w.stack.map((s) => (
                  <li
                    key={s}
                    className="rounded-full bg-secondary px-3 py-1 text-[13px] text-foreground/80 ring-1 ring-foreground/[0.05]"
                  >
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="space-y-10 md:col-span-7 md:col-start-6">
            {w.story.map((s) => (
              <div key={s.heading}>
                <h2 className="font-display text-2xl tracking-[-0.03em] md:text-3xl">{s.heading}</h2>
                <p className="mt-4 max-w-[60ch] text-pretty text-lg leading-relaxed text-muted-foreground">
                  {s.body}
                </p>
              </div>
            ))}
            <div>
              <h2 className="font-display text-2xl tracking-[-0.03em] md:text-3xl">In the product</h2>
              <ul className="mt-5 grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
                {w.built.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-lg leading-snug">
                    <span
                      aria-hidden
                      className="mt-[0.62em] h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ backgroundColor: w.accent }}
                    />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {w.gallery.length > 0 ? (
          <section aria-label="More screens" className="mx-auto max-w-[1400px] px-6 pb-20 md:px-10 md:pb-28">
            <div className={`grid gap-5 ${w.gallery.length > 1 ? "md:grid-cols-2" : ""}`}>
              {w.gallery.map((s) => (
                <div
                  key={s.src}
                  className="rounded-[2rem] bg-secondary/60 p-4 ring-1 ring-foreground/[0.06] sm:p-6 md:p-8"
                >
                  <BrowserFrame shot={s} host={w.host} dark={w.dark} />
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="mx-auto max-w-[1400px] border-t border-border/60 px-6 py-16 md:px-10 md:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-2xl tracking-[-0.03em] md:text-3xl">More work</h2>
            <Link
              to="/work"
              className="font-display text-base underline decoration-border underline-offset-4 transition-colors hover:decoration-foreground"
            >
              All work
            </Link>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {others.map((o, i) => (
              <WorkTile key={o.slug} item={o} index={i} />
            ))}
          </div>
        </section>

        <section className="border-t border-border/60 bg-foreground text-background">
          <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32">
            <h2
              className="max-w-[18ch] font-display leading-[1.02] tracking-[-0.035em]"
              style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
            >
              Want one of these for your product?
            </h2>
            <p className="mt-8 max-w-[48ch] text-lg leading-snug text-background/70 md:text-xl">
              Bring the idea or the half-built version. We will tell you what 14 days can do.
            </p>
            <div className="mt-10">
              <TalkToJonButton variant="light" />
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
